import { Logger } from '@shared/lib/logger';
import { persistLogEntries, restoreLogEntries } from './ring-buffer';
import { reportClientError } from '@shared/observability';
import { onAppHidden } from '@shared/lib/app-lifecycle';

// A dedicated Logger instance (not a bare pushLogEntry call) so these also
// get real console output and ring-buffer entries through the same path
// every other logger call does -- no special-casing.
const logger = new Logger('global-error', { error: 'rgb(255, 45, 85)' });

function toError(value: unknown, fallbackMessage: string): Error {
	return value instanceof Error ? value : new Error(fallbackMessage);
}

/**
 * Call once at app bootstrap (`main.tsx`). Installs the two global
 * listeners this repo has never had -- confirmed zero matches for either
 * repo-wide before this. Without them, a render crash outside an error
 * boundary or any rejected promise anywhere in the app vanishes with no
 * trace at all, not even in the console.
 *
 * Also owns the ring buffer's persistence, since this is already the
 * app-side bootstrap and the service worker must never run it (a SW global
 * scope has no `localStorage`).
 *
 * Each event feeds two places, deliberately: `logger.error()` for the
 * ring buffer/console, and
 * `reportClientError()` for the durable `cf-observability` event
 * (Section 16's pipeline) -- the same split every other client_error call
 * site already follows.
 */
export function installGlobalErrorCapture(): void {
	// Before any listener below can log: restored entries should sort ahead
	// of this session's, not be interleaved with them.
	restoreLogEntries();

	// The same save point `local-reporter.ts` flushes on.
	onAppHidden(persistLogEntries);

	window.addEventListener('error', (event) => {
		const error = toError(event.error, event.message);
		logger.error(error);
		reportClientError({ source: 'window', error });
	});

	window.addEventListener('unhandledrejection', (event) => {
		const error = toError(event.reason, String(event.reason));
		logger.error(error);
		reportClientError({ source: 'promise', error });
	});
}
